import { SWR_KEYS } from '@bulknutrients/bulk-shared-lib/constants';
import type { BulkAPIErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import { AutoPlayCarousel } from '@components/Carousels/Carousel';
import { Container } from '@components/Container';
import {
  DarkBackgroundOverrides,
  PortableTextBlockRenderer,
} from '@components/PortableText/PortableText';
import { SkipTo } from '@components/SkipTo/SkipTo';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { log } from '@lib/logs';
import type { ReactNode } from 'react';
import useSWR from 'swr';
import { twJoin } from 'tailwind-merge';

function Alert({ content }: { content: any }) {
  return (
    <div className={twJoin(['col-span-full row-span-full w-full text-center'])}>
      <PortableTextBlockRenderer
        content={content}
        componentsOverride={{
          ...DarkBackgroundOverrides,
          block: {
            normal: ({ children }: { children?: ReactNode }) => (
              <p className="m-0">{children}</p>
            ),
          },
        }}
      />
    </div>
  );
}

const NavLink = ({ link, message }) => {
  // return empty div so the alert is still centred
  if (!link) return <div />;
  return (
    <TextLink
      href={link}
      className="hidden text-nowrap lg:inline-block text-white hover:text-grey-mid"
    >
      {message}
    </TextLink>
  );
};

const fetcher = async (url: string) => {
  const res = await fetch(url);

  // If the status code is not in the range 200-299,
  // we still try to parse and throw it.
  if (res.status !== 200) {
    const {
      error: { message },
    } = (await res.json()) as BulkAPIErrorResponse;

    const error = new Error('Unable to fetch alerts');
    error.message = message.join('. ');

    throw error;
  }

  const { data } = await res.json();
  return data;
};
export function InfoNavbar() {
  const { data, error } = useSWR(SWR_KEYS.alerts, fetcher, {
    refreshInterval: 300000, // 5 minutes
    revalidateOnFocus: false,
    onError: (error) => {
      log({ error, location: 'InfoNavbar useSWR' });
    },
  });

  // If the height of this gets updated, you need to update the top position of the Modal in Autocomplete.tsx
  return (
    <Container className="bg-grey-darkest">
      <div className="full-bleed bg-transparent py-1 text-xs font-light leading-4 text-white">
        <div className="relative mx-auto my-0 block h-5 max-w-max-width px-2 py-1 sm:flex sm:justify-center lg:justify-between">
          <SkipTo />

          {data && !error && (
            <>
              <NavLink
                link={data.links.left.link}
                message={data.links.left.message}
              />

              <div className={twJoin(['header-alert', 'grow'])}>
                <AutoPlayCarousel
                  slides={data.alerts.map((alert: any, idx: number) => (
                    <Alert key={alert._id} content={alert.content} />
                  ))}
                />
              </div>

              <NavLink
                link={data.links.right.link}
                message={data.links.right.message}
              />
            </>
          )}
        </div>
      </div>
    </Container>
  );
}
